import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Button, Checkbox, FormControlLabel } from '@mui/material'
import {
  BillingStatusLabelsKeys,
  ReservationStatusLabelsKeys,
} from 'types/common'
import { CallMethodNames, SettlementNameProps } from 'types/common'
import { type ConsultationMethod } from 'types/applicant'
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { parseDatetimeSecondString } from 'ui/utils/dateFormatUtils'
import { subMinutes } from 'date-fns'
import { jstNewDate } from 'ui/utils/jstDatetime'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    width: '100%',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  finishMeetingRoot: {
    marginLeft: theme.spacing(1),
  },
  finishMeetingLabel: {
    fontSize: theme.typography.body2.fontSize,
  },
  finishMeetingCheckbox: {
    color: theme.palette.primary.main,
  },
  finishMeetingCheckboxDisabled: {
    opacity: 0.38,
  },
}))

type CloseDateSettingListProps = {
  /** 予約状況 */
  reservationStatus: ReservationStatusLabelsKeys

  /** 請求ステータス */
  billingStatus: BillingStatusLabelsKeys

  /** 決済方法 */
  settlement: SettlementNameProps | null

  /** 連絡方法 */
  callMethod?: CallMethodNames

  /** 受診方法 */
  consultationMethod?: ConsultationMethod | null

  /** 「予約の取消」ボタンのクリック時に呼ばれる */
  onClickCancel?: () => void

  /** 「ビデオ通話」ボタンのクリック時に呼ばれる クリックするとアプリごとのミーティングurlをAPIから取得しwindow.open()で開く */
  onClickStartVideoMeeting: (
    app: 'video_teams' | 'video_zoom'
  ) => Promise<void> | undefined

  /** 「通話完了」のチェックが変わった時に呼ばれる */
  onCheckMeetingFinished?: (meetingFinished: boolean) => void

  /** 「請求」ボタンのクリック時に呼ばれる */
  onClickBilling?: () => void

  /** 予約開始日時 */
  startTime: string

  /** 予約情報再送信ボタンを押した時 */
  onClickReSendReservationMail: () => void

  sending: boolean
}
const ReservationFooterControls: React.FC<CloseDateSettingListProps> = ({
  reservationStatus,
  billingStatus,
  settlement,
  callMethod,
  consultationMethod,
  startTime,
  onClickCancel,
  onClickStartVideoMeeting,
  onCheckMeetingFinished,
  onClickBilling,
  onClickReSendReservationMail,
  sending,
}: CloseDateSettingListProps) => {
  const { t } = useTranslation()
  const meetingFinished = (
    ['billed', 'finish', 'finish_no_billing'] as ReservationStatusLabelsKeys[]
  ).includes(reservationStatus)

  const cancelButtonDisabled = reservationStatus !== 'before'

  const hasBilling = settlement !== null
  const billed = billingStatus === 'billed'
  const showStartVideoButton =
    callMethod?.startsWith('video_') && consultationMethod !== 'in_person'
  const settlementIsCredit = settlement === 'credit_card'
  const billedButtonDisabled =
    !meetingFinished || (billed && settlementIsCredit)
  const billingButtonLabel =
    billed && !settlementIsCredit ? t('再請求') : t('請求')

  const { classes, cx } = useStyles()

  const [vcUnavailable, setVcUnavailable] = useState(true)
  const [urlLoading, setUrlLoading] = useState(false)
  useEffect(() => {
    if (!startTime) return
    const startDate = parseDatetimeSecondString(startTime)
    const msToCallable =
      subMinutes(startDate, 15).valueOf() - jstNewDate.valueOf()

    // NOTE: 通話可能になるまで待つ
    if (0 < msToCallable) {
      setVcUnavailable(true)
      const timerId = setTimeout(() => setVcUnavailable(false), msToCallable)
      return () => clearTimeout(timerId) // NOTE: defaultValuesが変わった際に走る
    } else {
      setVcUnavailable(false)
    }
  }, [startTime])

  // 予約取消済みの場合は非表示
  if (reservationStatus === 'canceled') {
    return null
  }

  return (
    <div className={classes.root}>
      <div>
        <Button
          color="secondary"
          onClick={onClickCancel}
          disabled={cancelButtonDisabled}
        >
          {t('予約の取消')}
        </Button>
        <Button
          color="primary"
          onClick={onClickReSendReservationMail}
          disabled={cancelButtonDisabled || sending}
        >
          {t('予約メールの再送信')}
        </Button>
      </div>

      <div>
        {showStartVideoButton && (
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              if (callMethod !== 'video_teams' && callMethod !== 'video_zoom') {
                return
              }
              setUrlLoading(true)
              onClickStartVideoMeeting(callMethod)?.finally(() =>
                setUrlLoading(false)
              )
            }}
            disabled={meetingFinished || vcUnavailable || urlLoading}
          >
            {t('ビデオ通話')}
          </Button>
        )}
        <FormControlLabel
          label={
            !!callMethod && consultationMethod !== 'in_person'
              ? t('通話完了')
              : t('実施完了')
          }
          classes={{
            root: classes.finishMeetingRoot,
            label: classes.finishMeetingLabel,
          }}
          control={
            <Checkbox
              color="primary"
              onChange={(_, checked) => {
                onCheckMeetingFinished?.(checked)
              }}
              checked={meetingFinished}
              disabled={billed}
              icon={
                <CheckBoxOutlineBlank
                  color="action"
                  classes={{
                    root: cx(billed && classes.finishMeetingCheckboxDisabled),
                  }}
                />
              }
              checkedIcon={
                <CheckBox
                  color="primary"
                  classes={{
                    root: cx(billed && classes.finishMeetingCheckboxDisabled),
                  }}
                />
              }
            />
          }
        />
        {hasBilling && (
          <Button
            color="primary"
            variant="contained"
            onClick={onClickBilling}
            disabled={billedButtonDisabled}
          >
            {billingButtonLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

export default ReservationFooterControls
